import React, { useCallback, useMemo } from "react";
import { ANALYTICS_EXPAND } from "../../../constants/modal.constant";
import { CRM_CHART_THEME } from "./crmAnalytics.constants";
import { Metric2 } from "../../../cores/boardAnalytics/interface/Metric2";
import { Metric2 as AnalyticsMetric2 } from "../../../cores/analytics/interface/Metric2";
import { useGlobalCore } from "../../../cores/globals";
import { Position } from "../../../cores/globals/interfaces/globalEntity";
import { AnalyticsExpand } from "./AnalyticsExpand";
import { CameFrom } from "./enum/CameFrom";

interface Props {
  metric: Metric2;
  cameFrom?: CameFrom;
}

export const CrmChartLegend = ({
  metric,
  cameFrom = CameFrom.boards,
}: Props) => {
  const { turnOnModal } = useGlobalCore();

  const getColor = useCallback(
    (index: number) => CRM_CHART_THEME[index % CRM_CHART_THEME.length],
    []
  );

  const total = useMemo(
    () =>
      metric.values?.reduce(
        (sum, item) => sum + (typeof item.value === "number" ? item.value : 0),
        0
      ) ||
      0,
    [metric.values]
  );

  const expandMetric = useMemo(
    (): AnalyticsMetric2 => ({
      ...metric,
      values:
        metric.values
          ?.filter((item) => typeof item.value === "number")
          .map((item) => ({ label: item.label, value: item.value as number })) ||
        [],
    }),
    [metric]
  );

  const handleOnClick = useCallback(() => {
    turnOnModal(
      ANALYTICS_EXPAND,
      <AnalyticsExpand metric={expandMetric} cameFrom={cameFrom} />,
      Position.LEFT
    );
  }, [expandMetric, cameFrom, turnOnModal]);

  if (!metric.values?.length) {
    return null;
  }

  return (
    <ul className="crm-legend">
      {metric.values.map(({ label, value }, index) => {
        const numericValue = typeof value === "number" ? value : 0;
        const pct = total > 0 ? Math.round((numericValue / total) * 100) : 0;
        const color = getColor(index);
        const formatted = metric.valueFormatter
          ? metric.valueFormatter(numericValue)
          : numericValue;

        return (
          <li
            key={`${label}-${index}`}
            className="crm-legend__item"
            onClick={handleOnClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleOnClick()}
          >
            <span
              className="crm-legend__dot"
              style={{ backgroundColor: color }}
            />
            <span className="crm-legend__label" title={label}>
              {label}
            </span>
            <span className="crm-legend__value">
              {formatted}
              {total > 0 && (
                <span className="crm-legend__pct">{pct}%</span>
              )}
            </span>
            <div className="crm-legend__bar">
              <span style={{ width: `${pct}%`, backgroundColor: color }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
};
